/**
 * Client-side rate limiter using a sliding window.
 * Defense-in-depth only — server-side rate limiting (Supabase dashboard) is the primary control.
 */

const windows = new Map<string, number[]>();

/**
 * Returns true if the action is allowed, false if rate-limited.
 * @param key   Unique identifier for the rate-limited action (e.g. 'auth:login')
 * @param max   Maximum number of attempts allowed in the window
 * @param windowMs  Window duration in milliseconds
 */
export function checkRateLimit(key: string, max: number, windowMs: number): boolean {
  const now = Date.now();
  const timestamps = (windows.get(key) ?? []).filter(t => now - t < windowMs);

  if (timestamps.length >= max) {
    windows.set(key, timestamps);
    return false;
  }

  timestamps.push(now);
  windows.set(key, timestamps);
  return true;
}

/**
 * Returns the number of seconds until the next attempt is allowed.
 * Returns 0 if not rate-limited.
 */
export function getRetryAfter(key: string, max: number, windowMs: number): number {
  const now = Date.now();
  const timestamps = (windows.get(key) ?? []).filter(t => now - t < windowMs);

  if (timestamps.length < max) return 0;

  const oldest = timestamps[0];
  return Math.ceil((oldest + windowMs - now) / 1000);
}
